import { Machine, assign } from "xstate";

const equipmentType = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isEquipmentTypeEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const manufacturer = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isManufacturerEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const model = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isModelEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const year = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isYearEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const serialNumber = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isSerialNumberEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const vin = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isVinEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const identifier = {
  initial: "noError",
  states: {
    noError: {
      initial: "serial",
      on: {
        INIT: [
          {
            cond: (context) => {
              return context.identifier === "serial";
            },
            target: "#step3.ready.identifier.noError.serial",
          },
          {
            cond: (context) => {
              return context.identifier === "vin";
            },
            target: "#step3.ready.identifier.noError.vin",
          },
          {
            cond: (context) => {
              return context.identifier === "both";
            },
            target: "#step3.ready.identifier.noError.both",
          },
        ],
        SUBMIT: [],
      },
      states: {
        serial: {},
        vin: {},
        both: {},
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const hoursMilesType = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isHoursMilesTypeEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const hoursMiles = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isHoursMilesEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const quantity = {
  initial: "noError",
  states: {
    noError: {
      on: {
        SUBMIT: [
          {
            cond: "isQuantityEmpty",
            target: "error.empty",
          },
        ],
      },
    },
    error: {
      initial: "empty",
      states: {
        empty: {},
      },
    },
  },
};

const images = {
  initial: "none",
  states: {
    none: {
      on: {
        "": [
          {
            target: "hasImages",
            cond: (context) => {
              return context && context.images && context.images.length > 0;
            },
          },
        ],
      },
    },
    hasImages: {},
  },
};

const identifierTypes = { both: "both", serial: "serial", vin: "vin" };

export const getStep3 = (values) => {
  const defaultValues = {
    equipmentType: "",
    manufacturer: "",
    model: "",
    year: undefined,
    serialNumber: "",
    identifier: identifierTypes.serial,
    vin: "",
    hoursMilesType: "hours",
    hoursMiles: undefined,
    quantity: undefined,
    images: [],
  };

  const context = {
    ...defaultValues,
    ...values,
  };

  if (!context.identifier) {
    context.identifier = identifierTypes.serial;
  }

  const config = {
    id: "step3",
    initial: "ready",
    context,
    states: {
      ready: {
        type: "parallel",
        on: {
          INPUT_EQUIPMENT_TYPE: {
            actions: "setEquipmentType",
            target: "ready.equipmentType.noError",
          },
          INPUT_MANUFACTURER: {
            actions: "setManufacturer",
            target: "ready.manufacturer.noError",
          },
          INPUT_MODEL: {
            actions: "setModel",
            target: "ready.model.noError",
          },
          INPUT_YEAR: {
            actions: "setYear",
            target: "ready.year.noError",
          },
          INPUT_IDENTIFIER: [
            {
              cond: (context, event) => {
                return event.identifier === identifierTypes.serial;
              },
              actions: "setIdentifier",
              target: [
                "ready.identifier.noError.serial",
                "ready.serialNumber.noError",
                "ready.vin.noError",
              ],
            },
            {
              cond: (context, event) => {
                return event.identifier === identifierTypes.vin;
              },
              actions: "setIdentifier",
              target: [
                "ready.identifier.noError.vin",
                "ready.serialNumber.noError",
                "ready.vin.noError",
              ],
            },
            {
              cond: (context, event) => {
                return event.identifier === identifierTypes.both;
              },
              actions: "setIdentifier",
              target: [
                "ready.identifier.noError.both",
                "ready.serialNumber.noError",
                "ready.vin.noError",
              ],
            },
          ],
          INPUT_SERIAL_NUMBER: {
            actions: "setSerialNumber",
            target: "ready.serialNumber.noError",
          },
          INPUT_VIN: {
            actions: "setVin",
            target: "ready.vin.noError",
          },
          INPUT_HOURS_MILES_TYPE: {
            actions: "setHoursMilesType",
            target: "ready.hoursMilesType.noError",
          },
          INPUT_HOURS_MILES: {
            actions: "setHoursMiles",
            target: "ready.hoursMiles.noError",
          },
          INPUT_QUANTITY: {
            actions: "setQuantity",
            target: "ready.quantity.noError",
          },
          ADD_IMAGES: {
            actions: "addImages",
            target: "ready.images.hasImages",
          },
          REMOVE_IMAGE: {
            actions: "removeImage",
          },
          SUBMIT: {
            cond: (_0, _1, meta) => {
              if (
                meta.state.matches("ready.equipmentType.error") ||
                meta.state.matches("ready.manufacturer.error") ||
                meta.state.matches("ready.model.error") ||
                meta.state.matches("ready.year.error") ||
                meta.state.matches("ready.serialNumber.error") ||
                meta.state.matches("ready.quantity.error") ||
                meta.state.matches("ready.hoursMilesType.error")
              ) {
                return false;
              }

              return true;
            },
            target: "finished",
          },
        },
        states: {
          equipmentType: {
            ...equipmentType,
          },
          manufacturer: {
            ...manufacturer,
          },
          model: {
            ...model,
          },
          year: {
            ...year,
          },
          serialNumber: {
            ...serialNumber,
          },
          vin: {
            ...vin,
          },
          identifier: {
            ...identifier,
          },
          hoursMilesType: {
            ...hoursMilesType,
          },
          hoursMiles: {
            ...hoursMiles,
          },
          quantity: {
            ...quantity,
          },
          images: {
            ...images,
          },
        },
      },
      noError: {},
      finished: {
        type: "final",
      },
      error: {
        equipmentType: {
          ...equipmentType,
        },
        manufacturer: {
          ...manufacturer,
        },
        model: {
          ...model,
        },
        year: {
          ...year,
        },
        serialNumber: {
          ...serialNumber,
        },
        vin: {
          ...vin,
        },
        identifier: {
          ...identifier,
        },
        hoursMilesType: {
          ...hoursMilesType,
        },
        quantity: {
          ...quantity,
        },
      },
      submitted: { onEntry: "complete" },
    },
  };

  function isEmpty(someString: string) {
    if (!someString || someString.length === 0) {
      return true;
    }

    return false;
  }

  const guardEmpty = (property) => (context: any, event: any) => {
    return isEmpty(context[property]);
  };

  const setProperty = (property: string) =>
    assign((context: any, event: any) => ({
      [property]: event[property],
    }));

  const guardEquipmentType = guardEmpty("equipmentType");
  const step3 = Machine(config as any, {
    actions: {
      setEquipmentType: setProperty("equipmentType"),
      setManufacturer: setProperty("manufacturer"),
      setModel: setProperty("model"),
      setYear: setProperty("year"),
      setSerialNumber: setProperty("serialNumber"),
      setVin: setProperty("vin"),
      setIdentifier: setProperty("identifier"),
      setHoursMilesType: setProperty("hoursMilesType"),
      setHoursMiles: setProperty("hoursMiles"),
      setQuantity: setProperty("quantity"),
      addImages: assign((context: any, event: any) => {
        const { value, name } = event.payload;
        const len = context.images.length;
        const newArr = value.map((file, ix) => ({
          name: `${name}-${len + ix}`,
          value: file,
        }));
        return {
          images: [...context.images, ...newArr],
        };
      }),
      removeImage: assign((context: any, event: any) => {
        let imageIndex = -1;

        context.images.some((image, index) => {
          if (image.name === event.name) {
            imageIndex = index;
            return true;
          }

          return false;
        });

        // clone the image array
        const result = [...context.images];

        // mutate the result array
        result.splice(imageIndex, 1);

        return {
          images: result,
        };
      }),
    },
    guards: {
      isEquipmentTypeEmpty: guardEmpty("equipmentType"),
      isManufacturerEmpty: guardEmpty("manufacturer"),
      isModelEmpty: guardEmpty("model"),
      isYearEmpty: guardEmpty("year"),
      isSerialNumberEmpty: (context: any, event: any) => {
        if (
          context.identifier === identifierTypes.serial ||
          context.identifier === identifierTypes.both
        ) {
          return isEmpty(context.serialNumber);
        }

        return false;
      },
      isVinEmpty: (context: any, event: any) => {
        if (
          context.identifier === identifierTypes.vin ||
          context.identifier === identifierTypes.both
        ) {
          return isEmpty(context.vin);
        }

        return false;
      },
      isHoursMilesTypeEmpty: guardEmpty("hoursMilesType"),
      isHoursMilesEmpty: guardEmpty("hoursMiles"),
      isQuantityEmpty: guardEmpty("quantity"),
      imagesEmpty: (context: any, event: any) => {
        return context.images.length < 1;
      },
    },
  });

  return step3;
};
